import { supabase } from '@/lib/supabase';

/**
 * The write actions, typed. This file is deliberately SEPARATE from
 * features/dashboard/data.ts and features/admin/data.ts, whose headers both
 * promise read-only; everything that can change the database goes through
 * here.
 *
 * Two shapes of write live in this file, by design:
 *
 *  - P3's five RPCs (submit / approve / deny / log / provisionManager's
 *    identity check) — definer functions, used wherever a write needs to do
 *    more than change one row (stamp the acting identity server-side,
 *    enqueue an email in the same transaction, enforce a transition rule).
 *    Through P3, these were the browser's ENTIRE write surface — no table
 *    held any INSERT/UPDATE/DELETE grant at all (tests/005 pinned it).
 *  - P4's admin writes — plain RLS-gated table writes (migration 0016's
 *    `adm_all` / `adm_write` policies), because an admin editing the
 *    Roster, Locations or Company settings is a single row change with
 *    nothing else that must happen atomically alongside it. The one
 *    exception, provisionManager(), calls an edge function because creating
 *    a sign-in account needs the secret key, which must never reach the
 *    browser — see supabase/functions/provision-manager/index.ts.
 *
 * THE RESULT CONTRACT — every write here resolves (never rejects) to an
 * ActionResult: `{ ok: true, ... }` or `{ ok: false, kind, error, detail? }`.
 *
 * Pre-P8 punch list, Tier 2 (error handling): the contract used to be
 * documented as "a refusal is a message, a rejected promise means something
 * real broke" — but nothing here ever rejected. toResult()/fromWrite()
 * folded EVERY transport error into `{ ok: false, error: error.message }`,
 * so a manager on bad shop wifi saw Chrome's bare "TypeError: Failed to
 * fetch" styled exactly like "That request is not pending". The UI could not
 * tell "the database said no, on purpose" from "the request never got
 * there", and neither could the manager. Now `kind` says which (see
 * ActionErrorKind), `error` is ALWAYS worded for a human, and the raw
 * technical text — when there is any worth keeping — rides along in
 * `detail` for a collapsed "Technical details" disclosure, never the
 * headline. `ok` stays `ok` (not renamed): it is the exact JSON shape every
 * definer RPC in supabase/migrations already returns, so renaming it here
 * would mean translating every response for no gain and diverging from the
 * database's own contract.
 */

export type ActionErrorKind = 'refusal' | 'failure' | 'stale';

export interface ActionResult {
  ok: boolean;
  /** Present when ok is false — ALWAYS worded for a human (the database's own wording for a refusal). */
  error?: string;
  /**
   * Present when ok is false — which kind of "no" this is, so the UI can
   * react appropriately:
   *   'refusal' — the database looked at the request and declined on purpose
   *               (a throttle, a duplicate day, out of scope, already decided,
   *               a lockout guard, a check constraint). Retrying the same
   *               thing will not help; the user needs to do something else.
   *   'failure' — the request itself broke: network, a missing function, an
   *               unexpected server exception, a malformed response. The
   *               change MAY have applied (a lost response looks identical to
   *               a lost request from here) — refresh to see, then retry.
   *   'stale'   — a plain table write matched no row: the record was changed
   *               or removed by someone else since the page loaded. Refresh,
   *               re-open, re-apply. (Produced by the .select()-checked table
   *               writes below once they carry the compare-and-set snapshot.)
   */
  kind?: ActionErrorKind;
  /** Raw technical text behind `error` (the wire message), when it adds anything — for a disclosure, never the headline. */
  detail?: string;
  /** Present on manager actions — the live portal's result wording. */
  message?: string;
  request_id?: string;
  status?: string;
  days?: number;
  days_requested?: number;
  date_range?: string;
}

/** The shape supabase-js hands back for a failed request — PostgREST's fields, all optional but message. */
interface WireError {
  message: string;
  code?: string | null;
  details?: string | null;
  hint?: string | null;
}

const FAILURE_TEXT =
  'Something went wrong talking to the server, so this may not have gone through. ' +
  'Check your connection, refresh to see the current state, then try again.';

/**
 * The browser's own fetch failure. USUALLY the request never left the phone
 * (offline, DNS, the gateway down) — but Chrome raises the same TypeError
 * for a connection reset or a wifi→cellular hop AFTER the body was sent, in
 * which case Postgres may have committed and only the answer was lost. So
 * "probably", never "nothing was saved" (review finding: a manager told
 * "nothing was saved" retries, and the RPC then says "already Approved").
 */
const OFFLINE_TEXT =
  'Could not reach the server, so this probably was not saved. Check your connection, ' +
  'refresh to check, then try again.';

/** PostgREST's PGRST301 with "expired" in it — the session, not the request, is the problem. */
const SESSION_EXPIRED_TEXT =
  'Your sign-in has expired, so this was not saved. Sign out and back in, then try again.';

/**
 * Any OTHER PGRST301: PostgREST could not verify the token — including the
 * "JWT issued at future" clock race this repo has already met (trap #19),
 * which clears on a plain retry. A sign-out remedy would be wrong and
 * expensive there.
 */
const SESSION_UNVERIFIED_TEXT =
  'Your sign-in could not be checked just now, so this was not saved. Try again in a moment.';

/**
 * The CHECK constraints an admin can actually trip from the console today,
 * named in the wire message, mapped to a sentence that says which field.
 * Anything else falls to the generic "rejected one of the values" line.
 */
const CONSTRAINT_SENTENCES: ReadonlyArray<readonly [string, string]> = [
  ['recipients_look_like_emails', 'One of the notification addresses does not look like an email address.'],
  // 0021. Reachable by a location-scoped manager since 0020, and worth a
  // plain sentence rather than the generic one: this is the address their
  // employee's approval and denial emails are sent to, so "check the fields"
  // would leave them guessing which field and why.
  ['employees_email_format', 'That email address does not look right — check for a typo, or leave it blank.'],
  ['thresholds_ascending', 'Point thresholds must be three numbers that go up, e.g. 10,15,20.'],
  ['thresholds_shape', 'Point thresholds must be three numbers that go up, e.g. 10,15,20.'],
];

/** A trigger's raise message as a headline: capitalised, ending in a full stop, like its RPC-authored neighbours. */
function asSentence(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return 'The database declined this change.';
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

/**
 * Classify a failed request by its SQLSTATE, the one signal that reliably
 * separates "the database declined" from "the request broke":
 *
 *   P0001  raise_exception — a trigger or RPC refused in its own human words
 *          (`managers_keep_one_admin`, `locations_no_orphans`, an RPC's own
 *          guard). Those messages were written to be shown; show them.
 *   42501  insufficient_privilege — an RLS `with check` refused an insert.
 *   23xxx  integrity (unique / FK / check) and 22xxx data (out of range) —
 *          the database rejected a VALUE. Its wording is for developers
 *          ("violates check constraint recipients_look_like_emails"), so
 *          headline a plain sentence and keep the raw text in `detail`.
 *   PGRST301 — PostgREST's own "JWT expired": the session, not the request,
 *          is the problem (Gemini's catch in the 3-party review). Say so.
 *   no code + the browser's fetch wording — the request never left the
 *          browser (offline, DNS, the gateway down). Nothing can have been
 *          saved, so say that plainly instead of the hedged FAILURE_TEXT.
 *   anything else — a transport-level failure (a 404 for a function hosted
 *          does not have yet, a 5xx, a timeout, a serialization abort). The
 *          change MAY have applied; headline the hedged sentence, raw text
 *          in `detail`.
 */
function fromError(wire: WireError): ActionResult {
  // postgrest-js JSON.parses ANY non-2xx JSON body into `error`, so a
  // gateway's `{"error": "..."}` arrives with no `message` at all despite
  // the type. The one thing an error handler must never do is throw.
  const message =
    typeof wire.message === 'string' ? wire.message : (JSON.stringify(wire) ?? String(wire));
  const code = wire.code ?? '';
  const m = message.toLowerCase();
  // Raw text for the disclosure — clamped so a 5xx HTML page never lands whole.
  const detail = message.slice(0, 500);
  if (code === 'P0001') return { ok: false, kind: 'refusal', error: asSentence(message) };
  if (m.includes('jwt expired')) {
    return { ok: false, kind: 'failure', error: SESSION_EXPIRED_TEXT, detail };
  }
  if (m.includes('issued at future')) {
    // CLAUDE.md trap #19: a clock race right after sign-in that clears on a
    // plain retry — the Dashboard's loader already retries it silently.
    return {
      ok: false,
      kind: 'failure',
      error: 'Your sign-in is still settling — wait a moment and try again.',
      detail,
    };
  }
  if (code === 'PGRST301') {
    return { ok: false, kind: 'failure', error: SESSION_UNVERIFIED_TEXT, detail };
  }
  if (
    !code &&
    (m.includes('failed to fetch') ||
      m.includes('networkerror') ||
      m.includes('network request failed') ||
      m.includes('load failed') ||
      // supabase-js's FunctionsFetchError wording for the same condition.
      m.includes('failed to send a request'))
  ) {
    console.error('[actions] request did not complete:', message);
    return { ok: false, kind: 'failure', error: OFFLINE_TEXT, detail };
  }
  if (code === '42501') {
    return {
      ok: false,
      kind: 'refusal',
      error: 'You do not have permission to make this change.',
      detail,
    };
  }
  if (code === '23505') {
    return {
      ok: false,
      kind: 'refusal',
      error: 'That would duplicate a record that already exists.',
      detail,
    };
  }
  if (code.startsWith('23') || code.startsWith('22')) {
    const known = CONSTRAINT_SENTENCES.find(([name]) => m.includes(name));
    return {
      ok: false,
      kind: 'refusal',
      error: known ? known[1] : 'The database rejected one of the values. Check the fields and try again.',
      detail,
    };
  }
  console.error('[actions] request failed:', message, wire.details ?? '', wire.hint ?? '');
  return { ok: false, kind: 'failure', error: FAILURE_TEXT, detail };
}

/** Unwrap a supabase.rpc() response into an ActionResult, uniformly. */
function toResult(data: unknown, error: WireError | null): ActionResult {
  if (error) return fromError(error);
  if (data && typeof data === 'object' && 'ok' in data) {
    const result = data as ActionResult;
    // The RPC's own `{ ok: false, error }` is a refusal by definition — that
    // is the whole reason the RPCs return messages instead of raising.
    return result.ok ? result : { ...result, kind: result.kind ?? 'refusal' };
  }
  console.error('[actions] unexpected response shape:', data);
  return {
    ok: false,
    kind: 'failure',
    error: FAILURE_TEXT,
    detail: `Unexpected response shape: ${JSON.stringify(data)?.slice(0, 200) ?? String(data)}`,
  };
}

/**
 * The employee submit (M2, §4.3) — callable with NO session; the open request
 * page is this function's whole reason to exist. Dates are YYYY-MM-DD strings
 * straight from <input type="date"> — never Date objects (trap #1).
 *
 * `resubmittedFrom` (0032) is the denied request id from the denial email's
 * "request a different date" link. The key is sent only when there is one, so
 * an ordinary submit stays the exact five-argument call it always was.
 */
export async function submitTimeOffRequest(args: {
  employeeName: string;
  startDate: string;
  endDate: string | null;
  ptoAmount: 'Full' | 'Half';
  notes: string | null;
  resubmittedFrom?: string | null;
}): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('submit_time_off_request', {
    p_employee_name: args.employeeName,
    p_start_date: args.startDate,
    p_end_date: args.endDate,
    p_pto_amount: args.ptoAmount,
    p_notes: args.notes,
    ...(args.resubmittedFrom ? { p_resubmitted_from: args.resubmittedFrom } : {}),
  });
  return toResult(data, error);
}

/** Active roster names for the request form's dropdown (0012). */
export async function requestFormNames(): Promise<string[]> {
  const { data, error } = await supabase.rpc('request_form_names');
  if (error) throw new Error(`request_form_names(): ${error.message}`);
  return (data ?? []) as string[];
}

/** Approve a Pending request. Stamps + emails happen server-side, atomically. */
export async function approveRequest(requestId: string): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('approve_request', { p_request: requestId });
  return toResult(data, error);
}

/** Deny a Pending request. A denied day releases its date for re-request. */
export async function denyRequest(requestId: string): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('deny_request', { p_request: requestId });
  return toResult(data, error);
}

// ---------------------------------------------------------------------------
// P4: the admin console's writes. Everything below is a PLAIN RLS-gated
// table write (migration 0016's `adm_all` / `adm_write` policies) — no RPC,
// because there is no server-side transaction to protect: unlike approve/
// deny/log (which enqueue an email in the same breath as the status change),
// an admin edit to the Roster, Locations or Company settings is a single row
// change with nothing else that has to happen atomically alongside it. The
// one exception is provisionManager(), which calls the provision-manager
// edge function because creating a sign-in account needs the secret key —
// see that function's header for why the split is deliberate.
// ---------------------------------------------------------------------------

function fromWrite(error: WireError | null): ActionResult {
  if (error) return fromError(error);
  return { ok: true };
}

/**
 * STALE-ROW DETECTION for the table writes below (pre-P8 punch list, Tier 2:
 * "plain-table writes report success even when the target row no longer
 * matches"). A plain UPDATE/DELETE whose WHERE matched nothing is a silent
 * no-op to PostgREST — 200, empty body — so this file used to say "saved"
 * when nothing was. Two layers, no schema change:
 *
 *  1. `.select(...)` on every update/delete, so the response carries the rows
 *     actually touched. Zero rows → `kind: 'stale'`, never `ok: true`.
 *  2. COMPARE-AND-SET against the row AS THE SCREEN LOADED IT: the caller
 *     passes the original row (`expect`), and every one of its columns
 *     joins the WHERE clause. If another admin changed ANY of them since
 *     this page loaded, the WHERE matches nothing and the write is refused
 *     as stale instead of silently overwriting their change (the classic
 *     lost update). This is optimistic locking with the whole row as the
 *     version — the only option here, since employees/managers/locations
 *     carry no updated_at column (migration 0016's own note) and adding
 *     one is a migration, out of this run's scope. company_settings DOES
 *     carry a trigger-maintained updated_at (0016 §2b), so it uses that.
 *
 * False conflicts need a loaded value to round-trip to a different
 * representation, which none of these column types do: text is exact,
 * numeric(5,2) compares 5 = 5.00, booleans are booleans, uuids are text,
 * arrays are compared as an exact array literal (order preserved, as the UI
 * writes and reads them), and NULL columns use IS NULL.
 *
 * On a miss, ONE cheap read by primary key tells "changed" from "removed" so
 * the sentence can say which (Gemini's suggestion in the 3-party review).
 */
type SnapshotValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;

/** The two filter methods matchSnapshot() needs, structurally — PostgrestFilterBuilder's own generics are heavier than this file needs. */
interface SnapshotFilterable<T> {
  eq(column: string, value: string | number | boolean): T;
  is(column: string, value: null): T;
}

/** A Postgres array literal for an exact compare: `{10,15,20}`, `{"a@x.com","b@y.com"}`, `{}`. */
function toArrayLiteral(values: ReadonlyArray<string | number>): string {
  const items = values.map((v) =>
    typeof v === 'number' ? String(v) : `"${String(v).replace(/(["\\])/g, '\\$1')}"`,
  );
  return `{${items.join(',')}}`;
}

/**
 * `columns` is typed against the snapshot's own keys, so a misspelled or
 * removed column fails typecheck instead of silently becoming `IS NULL` (which
 * would refuse every save as stale, forever) — review finding.
 */
function matchSnapshot<T extends SnapshotFilterable<T>, Expect extends object>(
  query: T,
  snapshot: Expect,
  columns: ReadonlyArray<keyof Expect & string>,
): T {
  const row = snapshot as Record<string, SnapshotValue>;
  let q = query;
  for (const column of columns) {
    const value = row[column];
    if (value === null || value === undefined) q = q.is(column, null);
    else if (Array.isArray(value)) q = q.eq(column, toArrayLiteral(value));
    else q = q.eq(column, value as string | number | boolean);
  }
  return q;
}

/**
 * Interpret a `.select()`-returning update/delete: an error, rows touched
 * (ok), or nothing touched — in which case one read by primary key says
 * whether the row was changed by someone else or removed altogether.
 */
async function fromRowWrite(
  data: unknown[] | null,
  error: WireError | null,
  /**
   * `deletable`: only managers can actually be deleted by an admin (the other
   * three tables carry no DELETE grant), so a vanished row elsewhere means
   * this admin can no longer SEE it — worded as such, not as "removed".
   */
  probe: { table: string; pk: string; id: string; deletable?: boolean },
  /** Names the record ("Nathan Bordner", "Main Location") — the form and its "Editing X" caption are gone by the time this is read. */
  what: string,
  plural = false,
): Promise<ActionResult> {
  if (error) return fromError(error);
  if (data && data.length > 0) return { ok: true };
  const check = await supabase.from(probe.table).select(probe.pk).eq(probe.pk, probe.id).limit(1);
  const gone = !check.error && (check.data ?? []).length === 0;
  // "someone else" alone read as a security worry at a shop with one real
  // admin, where the likeliest conflict is the same person in another tab.
  // Not "another admin" since migration 0020: a location-scoped manager can
  // edit their own location's roster too, so the other writer is no longer
  // necessarily an admin (adversarial-review finding).
  const who = '(by an admin or another manager, or by you in another tab)';
  const was = plural ? 'were' : 'was';
  let text: string;
  if (gone && probe.deletable) {
    // Hedged: the same 0-row probe also happens if THIS admin just lost admin
    // access (the row is hidden, not gone) — review finding.
    text = `${what} ${was} removed since this page loaded ${who} — or ${plural ? 'are' : 'is'} no longer visible to you — so your change was not saved. The list is refreshing.`;
  } else if (gone) {
    text = `${what} could not be found or ${plural ? 'are' : 'is'} no longer available to you, so your change was not saved. The page is refreshing.`;
  } else {
    // A process, not a state: this sentence renders in the same batch that
    // STARTS the refresh, and a row deactivated by the other admin may end up
    // behind "Show inactive" — so promise only that a refresh is under way.
    text = `${what} ${was} changed since this page loaded ${who}, so your change was not saved. The page is refreshing — check the latest values before re-applying your change.`;
  }
  return { ok: false, kind: 'stale', error: text };
}

export interface EmployeeWriteInput {
  full_name: string;
  email: string | null;
  location_id: string;
  pto_allotment: number | null;
  active: boolean;
}

/** Roster: add. */
export async function createEmployee(
  input: Omit<EmployeeWriteInput, 'active'>,
): Promise<ActionResult> {
  const { error } = await supabase.from('employees').insert({
    full_name: input.full_name,
    email: input.email,
    location_id: input.location_id,
    pto_allotment: input.pto_allotment,
  });
  return fromWrite(error);
}

const EMPLOYEE_SNAPSHOT_COLUMNS = [
  'full_name',
  'email',
  'location_id',
  'pto_allotment',
  'active',
] as const satisfies ReadonlyArray<keyof EmployeeWriteInput>;

/**
 * Roster: edit (including the deactivate toggle). `expect` is the row as the
 * screen loaded it; when given, the write only lands if the row still reads
 * exactly that (see matchSnapshot). Omit it only for a blind write that
 * genuinely does not care what it overwrites.
 */
export async function updateEmployee(
  id: string,
  patch: Partial<EmployeeWriteInput>,
  expect?: EmployeeWriteInput,
): Promise<ActionResult> {
  let q = supabase.from('employees').update(patch).eq('id', id);
  if (expect) q = matchSnapshot(q, expect, EMPLOYEE_SNAPSHOT_COLUMNS);
  const { data, error } = await q.select('id');
  return fromRowWrite(data, error, { table: 'employees', pk: 'id', id }, expect?.full_name ?? 'This employee');
}

export interface LocationWriteInput {
  name: string;
  active: boolean;
  notes: string | null;
  point_thresholds: number[] | null;
  notification_recipients: string[] | null;
  pto_allotment: number | null;
}

/** Locations: add. Overrides are set afterward via updateLocation. */
export async function createLocation(name: string): Promise<ActionResult> {
  const { error } = await supabase.from('locations').insert({ name });
  return fromWrite(error);
}

/**
 * Locations: edit — rename, deactivate/reactivate, or change an override.
 * `locations_no_orphans` (migration 0003) refuses to deactivate a location
 * still assigned to anyone active; that refusal surfaces here as a normal
 * `{ ok: false, kind: 'refusal', error }`, not a thrown exception — the
 * trigger's message already names who is still assigned.
 */
export async function updateLocation(
  id: string,
  patch: Partial<LocationWriteInput>,
  expect?: LocationWriteInput,
): Promise<ActionResult> {
  let q = supabase.from('locations').update(patch).eq('id', id);
  if (expect) q = matchSnapshot(q, expect, LOCATION_SNAPSHOT_COLUMNS);
  const { data, error } = await q.select('id');
  return fromRowWrite(data, error, { table: 'locations', pk: 'id', id }, expect?.name ?? 'This location');
}

const LOCATION_SNAPSHOT_COLUMNS = [
  'name',
  'active',
  'notes',
  'point_thresholds',
  'notification_recipients',
  'pto_allotment',
] as const satisfies ReadonlyArray<keyof LocationWriteInput>;

export interface CompanySettingsWriteInput {
  point_thresholds: number[];
  notification_recipients: string[];
  pto_allotment: number | null;
  notify_employee: boolean;
  portal_url: string | null;
}

/**
 * Company settings: edit. Always the one seeded singleton row (id = 1).
 * `expect.updated_at` is the row's trigger-maintained stamp as loaded
 * (company_settings_touch_updated_at, migration 0016 §2b) — the one admin
 * table with a real version column, so the compare-and-set uses it directly.
 * Pass the string PostgREST returned, untouched: parsing it through a JS Date
 * would drop the microseconds and never match.
 */
export async function updateCompanySettings(
  patch: Partial<CompanySettingsWriteInput>,
  expect?: { updated_at: string },
): Promise<ActionResult> {
  let q = supabase.from('company_settings').update(patch).eq('id', 1);
  if (expect) q = q.eq('updated_at', expect.updated_at);
  const { data, error } = await q.select('id');
  return fromRowWrite(data, error, { table: 'company_settings', pk: 'id', id: '1' }, 'Company settings', true);
}

export interface ManagerWriteInput {
  full_name: string;
  role: 'manager' | 'admin';
  location_id: string | null;
  active: boolean;
}

/**
 * Managers: edit role / location / active for an EXISTING manager. Never
 * touches auth.users — a managers row is authority, not a login (AP-D6);
 * deactivating here revokes access on the person's very next request, no
 * re-login and no deploy needed, same property the schema was built for.
 * `managers_keep_one_admin` (migration 0003) refuses to leave zero active
 * admins; that refusal surfaces as `{ ok: false, kind: 'refusal', error }`
 * here too.
 */
export async function updateManager(
  email: string,
  patch: Partial<ManagerWriteInput>,
  expect?: ManagerWriteInput,
): Promise<ActionResult> {
  let q = supabase.from('managers').update(patch).eq('email', email);
  if (expect) q = matchSnapshot(q, expect, MANAGER_SNAPSHOT_COLUMNS);
  const { data, error } = await q.select('email');
  return fromRowWrite(
    data,
    error,
    { table: 'managers', pk: 'email', id: email, deletable: true },
    expect?.full_name ?? 'This manager',
  );
}

const MANAGER_SNAPSHOT_COLUMNS = [
  'full_name',
  'role',
  'location_id',
  'active',
] as const satisfies ReadonlyArray<keyof ManagerWriteInput>;

/**
 * Managers: remove (ADMIN_PORTAL_HANDOFF §5.2). Guarded by the same trigger.
 * The one real hard-DELETE in the admin console — which is exactly why "0
 * rows matched" matters most here: a second admin (or a second tab) removing
 * the same row used to be told it succeeded.
 */
export async function removeManager(email: string, label?: string): Promise<ActionResult> {
  const { data, error } = await supabase.from('managers').delete().eq('email', email).select('email');
  return fromRowWrite(data, error, { table: 'managers', pk: 'email', id: email, deletable: true }, label ?? 'This manager');
}

/**
 * Managers: add. The one admin action that is NOT a plain table write — it
 * needs a real auth.users account (`createUser({ email_confirm: true })`,
 * open item #12), which needs the secret key, which must never reach the
 * browser. See supabase/functions/provision-manager/index.ts.
 */
export async function provisionManager(input: {
  email: string;
  fullName: string;
  role: 'manager' | 'admin';
  locationId: string | null;
}): Promise<ActionResult> {
  const { data, error } = await supabase.functions.invoke('provision-manager', {
    body: {
      email: input.email,
      full_name: input.fullName,
      role: input.role,
      location_id: input.locationId,
    },
  });
  // A functions.invoke error is transport-shaped (no SQLSTATE): the function
  // itself reports its refusals as a 200 with { ok: false } — trap #21's
  // "a refusal is a message, not an exception" contract.
  if (error) return fromError({ message: error.message });
  return toResult(data, null);
}

// ---------------------------------------------------------------------------
// P5: Manage Records — void / reverse / edit a decided record (DD2/DD3/DD6).
// Four definer RPCs, same reasoning as P3's: an UPDATE policy cannot express
// DD6's column-locking (dates/type/employee stay locked) without a trigger,
// and void/reverse need an email enqueued in the same transaction as the
// status change (DD5) — see migration 0017's header for the full comparison
// against the mgr_write/adm_all policies migration 0011 once gestured at.
// ---------------------------------------------------------------------------

/** DD2: void a decided (Approved/Denied/Recorded) record. Reason required. */
export async function voidAbsenceRequest(requestId: string, reason: string): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('void_absence_request', {
    p_request: requestId,
    p_reason: reason,
  });
  return toResult(data, error);
}

/**
 * F7 (migration 0026): the exit from Needs Documentation → Recorded.
 * `noteReceived: true` also sets doctors_note and recomputes the days' points
 * through points_for(); `false` records the absence as-is. Silent (DD5).
 */
export async function resolveDocumentation(requestId: string, noteReceived: boolean): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('resolve_documentation', {
    p_request: requestId,
    p_note_received: noteReceived,
  });
  return toResult(data, error);
}

/** DD6's Status field: flips Approved ⇄ Denied. Always emails (DD5). */
export async function reverseDecision(requestId: string): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('reverse_decision', { p_request: requestId });
  return toResult(data, error);
}

/**
 * DD6's PTO Days + Excused flag/reason, one absence DAY at a time. Silent —
 * DD5 names this a correction that never emails. `excused: null` leaves the
 * exclusion untouched; `true`/`false` sets or clears it.
 */
export async function editAbsenceDay(args: {
  dayId: string;
  ptoDays?: number | null;
  excused?: boolean | null;
  excusedReason?: string | null;
}): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('edit_absence_day', {
    p_day: args.dayId,
    p_pto_days: args.ptoDays ?? null,
    p_excused: args.excused ?? null,
    p_excused_reason: args.excusedReason ?? null,
  });
  return toResult(data, error);
}

/** DD6's Notes field. Silent, same reason as editAbsenceDay. */
export async function updateRequestNotes(requestId: string, notes: string): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('update_request_notes', {
    p_request: requestId,
    p_notes: notes,
  });
  return toResult(data, error);
}

/**
 * The Log an Absence form (all four live decisions). `absenceType` uses the
 * clean enum spellings; the RPC also accepts the Sheet-era 'Tardy / Late'.
 */
export async function logAbsence(args: {
  employeeName: string;
  absenceType: string;
  startDate: string;
  endDate: string | null;
  decision: 'Record' | 'Approve' | 'Deny' | 'Pending';
  lateHours: number;
  doctorsNote: boolean;
  notes: string | null;
  ptoAmount: 'Full' | 'Half';
}): Promise<ActionResult> {
  const { data, error } = await supabase.rpc('log_absence', {
    p_employee_name: args.employeeName,
    p_absence_type: args.absenceType,
    p_start_date: args.startDate,
    p_end_date: args.endDate,
    p_decision: args.decision,
    p_late_hours: args.lateHours,
    p_doctors_note: args.doctorsNote,
    p_notes: args.notes,
    p_pto_amount: args.ptoAmount,
  });
  return toResult(data, error);
}
